import { useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import {
  trackThreeDPerfSample,
  trackThreeDPerfSession,
} from '~/utils/analytics'
import type { GPUTier } from './gpuTier'

type Bucket = 'low' | 'med' | 'high'

type Props = {
  gpuTier: GPUTier
  /** Min frames in a burst before computing fps. */
  windowSize?: number
  /** Min ms between reports to the same bucket. */
  reportIntervalMs?: number
  /**
   * Called once when the device sustains an unacceptably low frame rate across
   * several render windows. The viewer uses this to drop into static mode
   * (freeze camera + disable animations). One-way latch — fires at most once.
   */
  onDowngrade?: () => void
}

/** Below this fps a render window counts as "struggling". */
const DOWNGRADE_FPS = 20
/** Consecutive struggling windows before we freeze the scene. */
const DOWNGRADE_WINDOWS = 2

function bucketFor(fps: number): Bucket {
  if (fps < 30) return 'low'
  if (fps < 50) return 'med'
  return 'high'
}

// Demand-mode aware: useFrame fires only on requested renders, so a long gap
// between calls is idle time, not slow rendering. We accumulate frame deltas
// only when consecutive frames land within IDLE_GAP_MS — that's a render burst
// (orbit drag, config change). Outside bursts we reset the sampler.
const IDLE_GAP_MS = 100

type SessionState = {
  totalDt: number
  totalFrames: number
  minFps: number
  bucketCounts: Record<Bucket, number>
}

function emptySession(): SessionState {
  return {
    totalDt: 0,
    totalFrames: 0,
    minFps: Infinity,
    bucketCounts: { low: 0, med: 0, high: 0 },
  }
}

export function FpsReporter({
  gpuTier,
  windowSize = 30,
  reportIntervalMs = 5000,
  onDowngrade,
}: Props) {
  const frameTimes = useRef<number[]>([])
  const lastFrameAt = useRef<number>(0)
  const lastReportAt = useRef<number>(0)
  const lastBucket = useRef<string>('')
  const session = useRef<SessionState>(emptySession())
  const sessionEmitted = useRef<boolean>(false)
  const lowStreak = useRef<number>(0)
  const downgraded = useRef<boolean>(false)
  // Keep latest callback without re-running the perf loop's effects.
  const onDowngradeRef = useRef(onDowngrade)
  onDowngradeRef.current = onDowngrade

  useEffect(() => {
    frameTimes.current = []
    lastFrameAt.current = 0
    lastReportAt.current = 0
    lastBucket.current = ''
    session.current = emptySession()
    sessionEmitted.current = false
    lowStreak.current = 0

    // Idempotent — fires at most once per session. Mobile backgrounding
    // is the primary lifecycle hook; beforeunload is a desktop fallback.
    const emitSession = () => {
      if (sessionEmitted.current) return
      const s = session.current
      if (s.totalFrames === 0) return
      sessionEmitted.current = true

      const avgFps = (1000 * s.totalFrames) / s.totalDt
      const counts = s.bucketCounts
      // Tie-break: lower bucket wins, biased toward surfacing perf issues.
      let dominant: Bucket = 'low'
      let maxCount = counts.low
      if (counts.med > maxCount) {
        dominant = 'med'
        maxCount = counts.med
      }
      if (counts.high > maxCount) {
        dominant = 'high'
        maxCount = counts.high
      }

      trackThreeDPerfSession({
        minFps: s.minFps === Infinity ? 0 : s.minFps,
        avgFps,
        dominantBucket: dominant,
        lowFrames: counts.low,
        totalFrames: s.totalFrames,
        gpuTier,
        url:
          typeof window !== 'undefined' ? window.location.pathname : '',
      })
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') emitSession()
    }
    const onBeforeUnload = () => emitSession()

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('beforeunload', onBeforeUnload)
      emitSession()
    }
  }, [gpuTier])

  useFrame(() => {
    const now = performance.now()
    const prev = lastFrameAt.current
    lastFrameAt.current = now
    if (prev === 0) return

    const dt = now - prev
    if (dt <= 0 || dt > IDLE_GAP_MS) {
      frameTimes.current = []
      return
    }

    const s = session.current
    s.totalDt += dt
    s.totalFrames += 1
    s.bucketCounts[bucketFor(1000 / dt)] += 1

    const buf = frameTimes.current
    buf.push(dt)
    if (buf.length > windowSize) buf.shift()
    if (buf.length < windowSize) return

    let sum = 0
    let worst = 0
    for (const v of buf) {
      sum += v
      if (v > worst) worst = v
    }
    const avgMs = sum / buf.length
    const fps = 1000 / avgMs
    const bucket = bucketFor(fps)

    if (fps < s.minFps) s.minFps = fps

    // Sustained-low-fps latch: freeze the scene into static mode once the
    // device fails to keep up across several consecutive render windows.
    if (!downgraded.current && onDowngradeRef.current) {
      if (fps < DOWNGRADE_FPS) {
        lowStreak.current += 1
        if (lowStreak.current >= DOWNGRADE_WINDOWS) {
          downgraded.current = true
          onDowngradeRef.current()
        }
      } else {
        lowStreak.current = 0
      }
    }

    const sinceLast = now - lastReportAt.current
    const bucketChanged = bucket !== lastBucket.current
    if (sinceLast < reportIntervalMs && !bucketChanged) return

    lastReportAt.current = now
    lastBucket.current = bucket
    trackThreeDPerfSample({
      fpsBucket: bucket,
      fps,
      gpuTier,
      worstFrameMs: worst,
    })
  })

  return null
}
